// Qué comprar.
//
// La pantalla que más fácil se convierte en horóscopo, y por eso la
// que más disciplina necesita: acá NO hay una sola frase sobre moda.
// Cada cosa que dice viene con el número que la respalda, calculado
// sobre tu armario real, y el número se puede discutir.
//
// El orden no es casual — va de lo urgente a lo opcional:
//
//   1. Zonas que no podés cubrir. Sin esto no hay outfit posible.
//   2. Prendas tuyas que no entran en nada. Plata que ya gastaste.
//   3. De qué dependés. Si esa única prenda se rompe, ¿qué pasa?
//   4. Lo gastado, ordenado por lo que te sirve.
//   5. Recién ahí: qué comprar, con cuánto suma cada cosa.
//
// Lo de comprar va ÚLTIMO a propósito. Las primeras cuatro secciones
// suelen resolverse sin gastar un peso.

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { cargarOpciones } from "./opciones";
import Visor from "./Visor";
import { fotoChica, fotoGrande } from "./foto";
import { t, tPlural, tCat, numero, unirY, useIdioma, tError } from "./i18n";
import { seTermina } from "./perfumesDatos";
import { IconoFrasco } from "./Iconos";

export default function Comprar({ recargarToken, onVerCombinaciones }) {
  // Re-renderiza si cambia el idioma: `tCat` se resuelve en el render.
  useIdioma();
  const [datos, setDatos] = useState(null);
  const [opciones, setOpciones] = useState(null);
  const [ocasion, setOcasion] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [ampliada, setAmpliada] = useState(null);

  useEffect(() => {
    cargarOpciones().then(setOpciones);
  }, []);

  /* ⚠️  MISMA GUARDIA QUE EN ARMARIO Y OUTFITS (ME-6, 2026-09-03).
   *
   * Acá el filtro es un desplegable de ocasión, así que las carreras
   * son menos frecuentes que con un buscador — pero cada respuesta
   * cuesta un análisis entero del armario, así que las lentas son
   * justo las que más tardan en volver. Cambiar de ocasión dos veces
   * seguidas puede dejar en pantalla el análisis de la primera con la
   * segunda seleccionada. Ver el comentario largo en Armario.js. */
  const pedidoVigente = useRef(0);

  const traer = useCallback(async () => {
    const mio = ++pedidoVigente.current;
    setCargando(true);
    try {
      const d = await api.queComprar(ocasion || undefined);
      if (mio !== pedidoVigente.current) return;
      setError("");
      setDatos(d);
    } catch (e) {
      if (mio !== pedidoVigente.current) return;
      setError(tError(e));
    } finally {
      if (mio === pedidoVigente.current) setCargando(false);
    }
  }, [ocasion]);

  useEffect(() => {
    traer();
  }, [traer, recargarToken]);

  if (cargando && !datos) return <p className="tenue">{t("comprar.analizando", "Analizando tu armario...")}</p>;
  if (error && !datos) return <div className="mensaje-error" role="alert">{error}</div>;
  if (!datos) return null;

  const abrir = (p) =>
    fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p });

  return (
    <section>
      <div className="barra-lote">
        <div>
          <strong>{numero(datos.prendasEnArmario)}</strong> {t("comprar.prendas", "prendas")} ·{" "}
          {/* ⚠️  `truncado` VIAJABA HASTA ACÁ Y NADIE LO MIRABA (2026-09-05).

              `contarPorPrenda` corta en 200.000 combinaciones y devuelve
              `truncado: true`. El módulo lo dice por escrito: "un número
              que dice 200.000 cuando en realidad son más es menos honesto
              que decir «es un mínimo»". La pantalla dibujaba ese 200.000
              como si fuera exacto.

              Medido con 176 prendas (60x60x56): la pantalla decía
              "200.000 combinaciones" cuando son 201.600. Con 460 prendas
              el total real son 2,4 millones y seguiría diciendo 200.000
              — doce veces menos. */}
          {/* ⚠️  `tPlural` Y NO `{numero} {t(...)}` (auditoría 2026-09-15,
              §2.6): ru.js guardaba UNA forma («комбинаций», la de many),
              así que «1 комбинаций» estaba mal; el árabe tiene seis
              formas y recibía una. Es la pantalla que sostiene «te
              empuja a no comprar» y «todo con el número». */}
          {datos.truncado
            ? t("comprar.masDe", "más de {n}", { n: tPlural("plural.combinaciones", datos.total) })
            : tPlural("plural.combinaciones", datos.total)}
        </div>
        {opciones && (
          <div className="orden">
            <label htmlFor="ocasion-compra">{t("comprar.para", "Para")}</label>
            <select
              id="ocasion-compra"
              value={ocasion}
              onChange={(e) => setOcasion(e.target.value)}
            >
              <option value="">{t("comprar.todo", "Todo")}</option>
              {opciones.categorias.map((c) => (
                <option key={c} value={c}>
                  {tCat("ocasion", c)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && <div className="mensaje-error" role="alert">{error}</div>}
      {cargando && <p className="tenue texto-mini">{t("comprar.recalculando", "Recalculando...")}</p>}

      {/* 1. Lo urgente */}
      {datos.zonasSinCubrir.length > 0 && (
        <div className="aviso">
          <p>
            <strong>{t("comprar.sinOutfit", "No puedes armar ningún outfit")}</strong>
            {/* ⚠️  ERA `datos.zonasSinCubrir.join(" y ")`, y eso son DOS
                fugas de idioma en una línea (2026-09-05): la conjunción
                castellana, y los valores crudos del servidor —"torso",
                "piernas", "pies"— que tienen traducción en los doce
                archivos desde siempre y nadie llamaba. En inglés se leía
                ": you have nothing to cover torso y piernas." */}
            {t("comprar.faltaCubrirZonas", ": te falta con qué cubrir {zonas}.", {
              zonas: unirY(datos.zonasSinCubrir.map((z) => tCat("zona", z))),
            })}
          </p>
          <p className="tenue texto-chico">
            {t("comprar.esElMinimo", "Esto no es una mejora, es el mínimo. Todo lo demás de esta pantalla depende de resolverlo.")}
          </p>
        </div>
      )}

      {/* 1b. Los perfumes que se terminan (22/9). Es la única compra que
          esta pantalla recomienda sin dudar: no es algo que te falta, es
          algo que usás y se acaba. */}
      <PerfumesQueSeTerminan recargarToken={recargarToken} />

      {/* 2. Lo que ya gastaste y no rinde */}
      <Seccion
        titulo={t("comprar.huerfanasTitulo", "Prendas que no entran en ninguna combinación")}
        vacio={t("comprar.huerfanasVacio", "Todas tus prendas entran en al menos un outfit.")}
        items={datos.huerfanas}
      >
        <p className="tenue texto-chico">
          {t("comprar.huerfanasExplica", "Cada una de estas o necesita una compañera, o es candidata a irse. Es plata que ya está gastada.")}
        </p>
        <div className="lista-prendas">
          {datos.huerfanas.map((p) => (
            <FilaPrenda key={p.id} prenda={p} onAmpliar={abrir}>
              {p.bloqueadaPorReglas ? (
                <span className="tenue">
                  {t("comprar.frenadaReglas", "El armario alcanza — la frenaron las reglas de estilo o un veto tuyo.")}{" "}
                  <strong>{t("comprar.noCompres", "No compres nada por esto.")}</strong>{" "}
                  {t("comprar.sinReglasEntraria", "Sin reglas entraría en {n}.", {
                    n: p.combinacionesSinReglas,
                  })}
                </span>
              ) : (
                <span>
                  {t("comprar.leFalta", "Le falta con qué cubrir")}{" "}
                  <strong>
                    {unirY((p.faltaCubrir || []).map((z) => tCat("zona", z))) ||
                      t("comprar.restoCuerpo", "el resto del cuerpo")}
                  </strong>.
                </span>
              )}
            </FilaPrenda>
          ))}
        </div>
      </Seccion>

      {/* 3. Dependencias */}
      <Seccion
        titulo={t("comprar.dependeTitulo", "De qué depende tu armario")}
        vacio={t("comprar.dependeVacio", "Ninguna prenda concentra más del 15% de tus combinaciones. Estás bien repartido.")}
        items={datos.criticas}
      >
        <p className="tenue texto-chico">
          {t("comprar.dependeExplica", "Si alguna de estas se rompe o se mancha, pierdes ese porcentaje de tus outfits de golpe. Son las que conviene tener repetidas.")}
        </p>
        <div className="lista-prendas">
          {datos.criticas.map((p) => (
            <FilaPrenda key={p.id} prenda={p} onAmpliar={abrir}>
              <strong>{numero(p.pctDelArmario)}%</strong> {t("comprar.deTusCombinaciones", "de tus combinaciones")} ·{" "}
              {tPlural("plural.outfits", p.combinaciones)}
              {onVerCombinaciones && (
                <button className="enlace-combis" onClick={() => onVerCombinaciones(p)}>
                  {" "}
                  {t("comprar.ver", "ver →")}
                </button>
              )}
            </FilaPrenda>
          ))}
        </div>
      </Seccion>

      {/* 4. Reemplazos */}
      <Seccion
        titulo={t("comprar.gastadasTitulo", "Gastadas, por orden de urgencia")}
        vacio={t("comprar.gastadasVacio", "No tienes nada marcado como Gastada.")}
        items={datos.reemplazos}
      >
        <p className="tenue texto-chico">
          {t("comprar.gastadasExplica", "Ordenadas por cuánto las usas. Una gastada que sostiene medio armario es urgente; una que entra en tres outfits puedes tirarla sin reemplazar.")}
        </p>
        <div className="lista-prendas">
          {datos.reemplazos.map((p) => (
            <FilaPrenda key={p.id} prenda={p} onAmpliar={abrir}>
              {tPlural("plural.combinaciones", p.combinaciones)} ·{" "}
              {p.pctDelArmario}% {t("comprar.delArmario", "del armario")}
            </FilaPrenda>
          ))}
        </div>
      </Seccion>

      {/* 5. Recién ahora, comprar */}
      <h2 className="seccion-titulo">{t("comprar.queConviene", "Qué te conviene comprar")}</h2>
      <p className="tenue texto-chico">
        {t("comprar.comoSeCalcula", "Cada número es real: se simula agregar esa prenda a tu armario y se vuelve a contar, respetando tus vetos y las reglas de estilo. No es una opinión sobre moda.")}
      </p>

      <div className="grilla-compras">
        {datos.recomendaciones.map((r) => (
          <Recomendacion key={r.tipo} r={r} total={datos.total} truncado={datos.truncado} />
        ))}
      </div>

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}

/* ------------------------------------------------------------------
   Piezas
------------------------------------------------------------------ */

// Una sección que cuando está vacía dice por qué, en vez de
// desaparecer. "No tenés prendas huérfanas" es información buena; que
// falte el bloque sin explicación es sospecha de que algo se rompió.
function Seccion({ titulo, vacio, items, children }) {
  return (
    <div className="seccion">
      <h2 className="seccion-titulo">{titulo}</h2>
      {items.length === 0 ? (
        <p className="tenue texto-chico">{vacio}</p>
      ) : (
        children
      )}
    </div>
  );
}

function FilaPrenda({ prenda, onAmpliar, children }) {
  return (
    <div className="fila-prenda">
      {fotoChica(prenda) ? (
        <img loading="lazy" decoding="async"
          src={fotoChica(prenda)}
          alt=""
          className="fila-prenda-foto"
          onClick={() => onAmpliar(prenda)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            /* Enter y espacio hacen lo mismo que el click, sea lo que
               sea. No se duplica la expresión del onClick: se dispara
               el click de verdad. Copiar el handler exigía parsear JSX
               con llaves anidadas, que es justamente lo que este
               proyecto ya aprendió a no hacer. */
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        />
      ) : (
        <div className="fila-prenda-foto fila-prenda-vacia" />
      )}
      <div className="fila-prenda-texto">
        <p className="texto-mini" style={{ margin: 0 }}>
          <strong>{prenda.nombre}</strong>
        </p>
        <p className="texto-mini" style={{ margin: 0 }}>
          {children}
        </p>
      </div>
    </div>
  );
}

function Recomendacion({ r, total, truncado }) {
  const m = r.mejor;
  const nulo = m.desbloquea <= 0;

  return (
    <article className={`compra ${nulo ? "compra-nula" : ""}`}>
      <div className="compra-cabeza">
        <span className="compra-tipo">{nombreDeTipo(r.tipo)}</span>
        <span className={`compra-numero ${nulo ? "tenue" : ""}`}>
          {m.desbloquea > 0 ? `+${numero(m.desbloquea)}` : "+0"}
        </span>
      </div>

      <p className="texto-mini" style={{ margin: "0 0 4px" }}>
        {m.color === "neutro" ? (
          <>
            {t("comprar.en", "En")} <strong>{t("comprar.neutro", "neutro")}</strong>{" "}
            <span className="tenue">
              ({(m.equivalentes || []).slice(0, 5).map((c) => tCat("color", c)).join(", ")}…)
            </span>
          </>
        ) : (
          <>
            {t("comprar.en", "En")} <strong>{tCat("color", m.color)}</strong>
          </>
        )}
      </p>

      {nulo ? (
        <p className="texto-mini tenue" style={{ margin: 0 }}>
          {t("comprar.noSuma", "No te suma nada: ya tienes de sobra para esta zona.")}
        </p>
      ) : (
        <>
          <p className="texto-mini tenue" style={{ margin: 0 }}>
            {t("comprar.pasarias", "Pasarías de {antes} a {despues} combinaciones ({pct}% más).", {
              antes: numero(total),
              despues: numero(m.totalDespues),
              pct: m.pctGanado,
            })}
            {/* El "+N" de arriba es exacto —se cuenta anclando en la
                prenda nueva y en modo exhaustivo—. El que puede estar
                cortado es el TOTAL, y con él el porcentaje. Se dice acá
                en vez de callarlo. */}
            {truncado && (
              <>
                {" "}
                <span className="tenue">
                  {t("comprar.totalAproximado", "Tu armario es grande y la cuenta se corta antes de terminar: el total es un mínimo, no un número exacto.")}
                </span>
              </>
            )}
          </p>
          {m.rescatadas.length > 0 && (
            <p className="texto-mini" style={{ margin: "4px 0 0" }}>
              {t("comprar.ademasRescata", "Además rescata")}{" "}
              {tPlural("plural.prendas", m.rescatadas.length)}{" "}
              {t("comprar.queHoyNoUsas", "que hoy no usas:")}{" "}
              <span className="tenue">
                {m.rescatadas.slice(0, 3).map((x) => x.nombre).join(", ")}
              </span>
            </p>
          )}
        </>
      )}

      {r.alternativas.length > 0 && !nulo && (
        <p className="texto-mini tenue" style={{ margin: "6px 0 0" }}>
          {t("comprar.otrosColores", "Otros colores:")}{" "}
          {r.alternativas
            /* `a.color` es la etiqueta de lib/compras.js: o el literal
             * "neutro" (el grupo) o un color del catálogo. Cada uno se
             * traduce por su camino. */
            .map((a) => {
              const nombre =
                a.color === "neutro" ? t("comprar.neutro", "neutro") : tCat("color", a.color);
              return `${nombre} +${numero(a.desbloquea)}`;
            })
            .join(" · ")}
        </p>
      )}
    </article>
  );
}

// Los tipos internos son categorías de zona, no palabras que alguien
// use para hablar de ropa. "Comprate un Inferior" no se le dice a nadie.
function nombreDeTipo(tipo) {
  return (
    {
      Superior: t("comprar.tipoSuperior", "Algo para arriba"),
      Inferior: t("comprar.tipoInferior", "Algo para abajo"),
      Calzado: t("comprar.tipoCalzado", "Calzado"),
      Enterizo: t("comprar.tipoEnterizo", "Un enterizo o vestido"),
    }[tipo] || tCat("tipo", tipo)
  );
}

/* ------------------------------------------------------------------
   «Se te está terminando X» (22/9)

   Pide su propia lista y no toca el cálculo de compras: el motor
   trabaja con prendas y combinaciones, y un frasco no combina con
   nada. Si la lista no llega (la base sin `perfumes.sql`, un corte), no
   se dibuja nada: es un aviso extra, no una parte de la cuenta.
------------------------------------------------------------------ */
function PerfumesQueSeTerminan({ recargarToken }) {
  useIdioma();
  const [perfumes, setPerfumes] = useState([]);
  useEffect(() => {
    let vivo = true;
    api
      .listarPerfumes()
      .then((d) => vivo && setPerfumes(d.perfumes || []))
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [recargarToken]);

  const pocos = perfumes.filter(seTermina).sort((a, b) => a.nivel - b.nivel);
  if (!pocos.length) return null;

  return (
    <div className="aviso perfumes-terminan">
      <p>
        <strong>{t("comprar.perfumesTerminan", "Se te está terminando")}</strong>
      </p>
      <ul className="perfumes-terminan-lista">
        {pocos.map((p) => (
          <li key={p.id}>
            <IconoFrasco tamano={14} relleno />{" "}
            {t("comprar.perfumeNivel", "{nombre}: queda {n}%", { nombre: p.nombre, n: p.nivel })}
          </li>
        ))}
      </ul>
    </div>
  );
}
